import { type ComponentProps, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  FlatList,
  Platform,
  Pressable,
  RefreshControl,
  useColorScheme,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { Paragraph, XStack, YStack } from 'tamagui';
// Lucide replaces the hand-rolled AlertIcon/MessageIcon/ThreadIcon
// single-glyph components (identical lucide marks).
import {
  AlertCircle as AlertIcon,
  MessageSquare as MessageIcon,
  MessageSquare as ThreadIcon,
} from 'lucide-react-native';

import { Text } from '@/components/text';
import { Linkified } from '@/components/linkified';
import { Badge } from '@/components/badge';
import { cardStyle } from '@/components/card';
import { EmptyState } from '@/components/empty-state';
import { useGlassAccentColor } from '@/components/glass-surface';
import { Heading } from '@/components/heading';
import { OverflowMenu } from '@/components/overflow-menu';
import { AttachmentList } from '@/components/attachment-list';
import { AttachmentPicker } from '@/components/attachment-picker';
import { PrimaryButton } from '@/components/primary-button';
import { PseudonymousAvatar } from '@/components/pseudonymous-avatar';
import { ScreenContainer } from '@/components/screen-container';
import { useDockInset, useDockScroll } from '@/lib/dock';
import { hit, slopFor } from '@/lib/hit-slop';
import { ScreenHeader } from '@/components/screen-header';
import { StaleRefreshNotice } from '@/components/resource-list-view';
import { Sheet } from '@/components/sheet';
import { VoteControl } from '@/components/vote-control';
import { useMyProfile } from '@/hooks/use-my-profile';
import { useVote } from '@/hooks/use-vote';
import { Skeleton } from '@/components/skeleton';
import { TextArea } from '@/components/text-area';
import { WebRefreshButton } from '@/components/web-refresh';
import { useAuth } from '@/hooks/auth-context';
import { useAttachmentPicker } from '@/hooks/use-attachment-picker';
import { api, ApiError, type PostItem, type ThreadListItem } from '@/lib/api';
import { hasSendableContent } from '@/lib/attachments';
import { displayHandle } from '@/lib/avatar-identity';
import { MAX_POST_BODY_LEN } from '@/lib/content-limits';
import { distanceMeters, formatDistance, type Coordinates } from '@/lib/distance';
import { fetchAllPosts } from '@/lib/fetch-all-pages';
import { queryKeys } from '@/lib/query-keys';
import { relativeTime } from '@/lib/relative-time';
import { useShadow } from '@/theme/shadow';
import { darkColors, lightColors, radius, space } from '@/theme/tokens.generated';

// Real screen, not a stub -- issue #15's detail slice. useLocalSearchParams
// in the route file above types `id` as `string | string[] | undefined`
// (Expo Router's generic param typing) -- threadId here is narrowed to
// `string | undefined` since this route is never matched without an id
// segment; the `undefined` case only exists for the render before the
// first navigation params are available. With `enabled: false` a
// TanStack Query v5 query's own status stays 'pending' rather than
// settling into anything else, and thread.isPending (checked first
// below) renders the loading spinner for it -- practically unreachable
// on a real [id] route (params are available synchronously in
// practice), so no separate handling was added for it, but noting
// precisely what happens rather than the "handled the same way as a
// failed load" this comment used to claim, which wasn't accurate
// (final review, PR #51).
export function ThreadDetail({ threadId }: { threadId: string | undefined }) {
  const dockInset = useDockInset();
  const raised = useShadow(2);
  const dockScroll = useDockScroll();
  const router = useRouter();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const currentUserId = auth.status === 'signed-up' ? auth.user.id : undefined;
  const [replyBody, setReplyBody] = useState('');
  const attachments = useAttachmentPicker();
  // Issue #291: the reply composer used to be an ever-present box
  // pinned to the bottom of the screen -- founder, real iPhone
  // screenshot, 2026-09-18: "posting should be hidden behind a small
  // floating button in the bottom right until you scroll to the end".
  // composerOpen gates the Sheet the FAB below opens into.
  const [composerOpen, setComposerOpen] = useState(false);
  // Tracks whether the reply list's own scroll position is at (or
  // within reach of) its end, to decide whether the FAB renders at
  // all. A SHORT or EMPTY reply list (contentHeight <= layoutHeight)
  // counts as already "at the end" -- there is nothing to scroll
  // through, so the reply affordance must still be reachable without
  // requiring a scroll gesture that can never actually happen.
  const [scrollMetrics, setScrollMetrics] = useState({ contentHeight: 0, layoutHeight: 0, offsetY: 0 });
  const nearEnd =
    scrollMetrics.contentHeight <= scrollMetrics.layoutHeight ||
    scrollMetrics.contentHeight - scrollMetrics.layoutHeight - scrollMetrics.offsetY < 80;

  function handleScroll(e: NativeSyntheticEvent<NativeScrollEvent>) {
    setScrollMetrics((m) => ({ ...m, offsetY: e.nativeEvent.contentOffset.y }));
  }

  // Native semantic color, not a Tamagui token -- same reasoning as
  // threads/index.tsx's identical RefreshControl setup.
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;

  const thread = useQuery({
    queryKey: queryKeys.thread(threadId as string),
    queryFn: () => api.getThread(threadId as string),
    enabled: threadId !== undefined,
    // A 404 means "this thread doesn't exist or was removed" -- a
    // stable outcome, not a transient failure worth retrying (same
    // reasoning as the thread list's 400-on-no-profile precedent).
    retry: false,
  });

  const posts = useQuery({
    queryKey: queryKeys.threadPosts(threadId as string),
    // listPosts returns one {items, nextCursor} page at a time; this
    // screen doesn't wire up infinite-scroll UI for replies (out of
    // scope, issue #138 only covers the Threads list), but it still
    // needs the COMPLETE reply list (pre-pagination behavior), so
    // fetchAllPosts loops through every cursor page and hands back a
    // flat array.
    queryFn: () => fetchAllPosts(threadId as string),
    enabled: threadId !== undefined,
    // retry:false to match the sibling `thread` query above (issue #126
    // AC5), which had it and this didn't -- the audit's own complaint
    // was the inconsistency itself, not a claim that false is strictly
    // correct for a posts fetch. This is a real trade-off, not a free
    // win: unlike `thread`'s 404-is-a-stable-outcome case, a posts
    // fetch failing is just as likely to be a transient network blip,
    // which React Query's default retries exist to smooth over -- this
    // screen now surfaces that immediately instead. The more correct
    // fix is a retry policy that distinguishes 404 (don't retry) from
    // network/5xx (do) -- ApiError already carries `status` for that --
    // left as a follow-up rather than done here, since this task was
    // scoped to "consistent," not a retry-policy redesign.
    retry: false,
  });

  // Own coordinates for ThreadHeader's distance line -- same
  // "stable 404, no retry" reasoning as the threads list's own-profile
  // query; the policy lives in useMyProfile now. Loading/error here
  // never blocks the thread itself from rendering; viewerCoords below
  // is simply `undefined` (distance line omitted) until/unless this
  // succeeds.
  const ownProfile = useMyProfile();
  // One hook per target type: the thread's own vote rides the
  // ['thread', id] cache, replies ride the bare-array
  // ['thread', id, 'posts'] cache (issue #241).
  const threadVote = useVote('thread');
  const postVote = useVote('post');
  const viewerCoords: Coordinates | undefined = ownProfile.isSuccess
    ? { lat: ownProfile.data.lat, lng: ownProfile.data.lng }
    : undefined;

  const reply = useMutation({
    mutationFn: () => api.createPost(threadId as string, replyBody.trim(), attachments.photoIds),
    onSuccess: () => {
      // Invalidate rather than optimistically append -- CreatePostHandler
      // is the source of truth for the post's real id/createdAt, and
      // this thread's post volume is low enough that a refetch is cheap.
      queryClient.invalidateQueries({ queryKey: queryKeys.threadPosts(threadId as string) });
      setReplyBody('');
      attachments.reset();
      // Closes the Sheet the FAB opened -- a posted reply is done, no
      // reason to leave the composer open over the list it just added
      // to.
      setComposerOpen(false);
    },
  });

  // Not while a photo is still uploading: it would be posted without it.
  const canReply = hasSendableContent(replyBody, attachments.photoIds.length) && !attachments.isUploading;
  const threadNotFound = thread.isError && thread.error instanceof ApiError && thread.error.status === 404;

  return (
    <ScreenContainer scrollUnderDock>
      {/* Same Android reasoning as create-thread's own composer -- see
          that file's comment (Expo's default adjustResize already
          handles it; RN's own 'height' would double-apply). */}
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <YStack flex={1}>
          {/* This screen's own way out -- (app)/_layout.tsx now sets
              headerShown: false for threads/[id] (its hardcoded native
              title, the literal word "Thread", sat directly above this
              screen's own real `thread.title` heading below, reading as
              an unpolished placeholder rather than the thread's actual
              title -- real user feedback on the redeployed app,
              2026-09-04). Removing the header also removes its back
              button, the only way out on web -- same shared BackLink
              (src/components/back-link.tsx) create-thread/sign-up/
              profile-view/reveal-requests/report-target/block-user all
              use, "Back" rather than "Cancel" since this dismisses a
              real, already-posted thread, not an in-progress draft (see
              BackLink's own comment for why the label varies while the
              treatment doesn't, and the accessible-name pitfall a
              leading chevron glyph caused here previously). Rendered
              even while the thread is still loading/erroring so
              there's always a way out, not just on the success path. */}
          {/* Refresh moved inline next to Back (issue #290) -- it used
              to sit inside ListHeaderComponent, above ThreadHeader,
              scrolling away with the rest of the list content instead
              of staying with the screen's own top-of-page chrome. */}
          <YStack>
            <ScreenHeader
              action={
                thread.data ? (
                  <WebRefreshButton
                    onRefresh={() => {
                      void thread.refetch();
                      void posts.refetch();
                    }}
                    refreshing={thread.isRefetching || posts.isRefetching}
                  />
                ) : undefined
              }
              onBack={() => router.back()}
            />
          </YStack>

          {thread.isPending ? (
            <ThreadDetailSkeleton />
          ) : threadNotFound ? (
            // Unconditional (not gated on !thread.data) on purpose,
            // unlike the generic error branch below -- a real 404 means
            // the thread was actually deleted, which is true regardless
            // of whatever's cached from before. Showing stale content
            // for a thread that's genuinely gone would be the "feedback
            // outruns the truth" bug in the other direction.
            <EmptyState
              icon={<ThreadIcon size={24} strokeWidth={1.75} color={colors.actionPrimary} />}
              title="This thread is gone"
              description="It may have been removed. Head back to see what else is nearby."
              action={{ label: 'Back to threads', onPress: () => router.push('/') }}
            />
          ) : thread.isError && !thread.data ? (
            // !thread.data, not just isError -- a background refetch
            // (a failed pull-to-refresh) sets status to 'error' while
            // leaving previously-loaded data in place (TanStack Query's
            // own behavior). Gating this screen-swap on the absence of
            // data means a failed pull only reaches this branch on a
            // genuine first-load failure -- caught in review of the
            // pull-to-refresh PR: without this guard, one failed pull
            // unmounted the whole thread, its replies, AND the reply
            // composer, even though all of it was still valid and cached.
            <EmptyState
              icon={<AlertIcon size={24} strokeWidth={1.75} color={colors.actionPrimary} />}
              title="Couldn't load this thread"
              description="Check your connection and try again."
              action={{ label: 'Try again', onPress: () => thread.refetch() }}
            />
          ) : thread.data ? (
            <>
              {/* Same reasoning as every screen's identical
                  notice (copy written once in
                  components/resource-list-view.tsx) -- a background
                  refetch failed but real content is still on screen,
                  so say so rather than silently keeping stale content
                  with no signal. pt (not the notice's usual pb) because
                  this screen's notice sits directly under the Back row,
                  with the list right below it. */}
              {thread.isError && <StaleRefreshNotice pt="$2" />}
              <YStack flex={1} position="relative">
                <FlatList
                  style={{ flex: 1 }}
                  // posts.data ?? [], not posts.isSuccess ? posts.data : []
                  // -- isSuccess (derived from query status) flips false
                  // on a background refetch failure even though posts.data
                  // itself is untouched, so the old isSuccess-gated version
                  // emptied the whole reply list into the "no replies yet"
                  // empty state the moment a pull-to-refresh failed, even
                  // though the real replies were still cached and valid
                  // (same class of bug as the two branches above).
                  data={posts.data ?? []}
                  keyExtractor={(post) => post.id}
                  contentContainerStyle={{ paddingBottom: space[16] + space[8] + dockInset, gap: space[2] }}
                  onScroll={(event) => {
                    handleScroll(event);
                    dockScroll.onScroll(event);
                  }}
                  scrollEventThrottle={16}
                  onContentSizeChange={(_w, h) => setScrollMetrics((m) => ({ ...m, contentHeight: h }))}
                  onLayout={(e) => setScrollMetrics((m) => ({ ...m, layoutHeight: e.nativeEvent.layout.height }))}
                  ListHeaderComponent={
                    <ThreadHeader
                      thread={thread.data}
                      viewerCoords={viewerCoords}
                      currentUserId={currentUserId}
                      replyCount={posts.data?.length}
                      voteProps={{
                        score: thread.data.score ?? 0,
                        viewerVote: thread.data.viewerVote,
                        onVote: (direction) => threadVote.mutate({ id: thread.data.id, direction }),
                        onRemove: () => threadVote.mutate({ id: thread.data.id }),
                      }}
                      router={router}
                    />
                  }
                  ListEmptyComponent={<PostsListStatus posts={posts} />}
                  renderItem={({ item }) => (
                    <PostCard
                      post={item}
                      currentUserId={currentUserId}
                      router={router}
                      voteProps={{
                        score: item.score ?? 0,
                        viewerVote: item.viewerVote,
                        onVote: (direction) => postVote.mutate({ id: item.id, direction }),
                        onRemove: () => postVote.mutate({ id: item.id }),
                      }}
                    />
                  )}
                  // Refetches both the thread and its replies -- a pull
                  // here is "refresh what I'm looking at", not just the
                  // list underneath the header. isRefetching (not
                  // isFetching) on both so the initial load's own
                  // spinners (thread.isPending above, posts.isPending in
                  // PostsListStatus) aren't double-counted as a refresh.
                  refreshControl={
                    <RefreshControl
                      refreshing={thread.isRefetching || posts.isRefetching}
                      onRefresh={() => {
                        thread.refetch();
                        posts.refetch();
                      }}
                      tintColor={colors.actionPrimary}
                      colors={[colors.actionPrimary]}
                      progressBackgroundColor={colors.surfacePage}
                    />
                  }
                />
                {/* Issue #291, founder feedback (real iPhone screenshot,
                    2026-09-18): "posting should be hidden behind a small
                    floating button in the bottom right until you scroll
                    to the end" -- replaces the old ever-present composer
                    box. nearEnd covers both "scrolled to the bottom of a
                    long list" and "the list is short/empty, nothing to
                    scroll through" (this hook's own comment above). */}
                {nearEnd && (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Reply to this thread"
                    onPress={() => setComposerOpen(true)}
                    style={{
                      position: 'absolute',
                      right: 20,
                      bottom: 20 + dockInset,
                      width: 52,
                      height: 52,
                      borderRadius: radius.full,
                      backgroundColor: colors.actionPrimary,
                      alignItems: 'center',
                      justifyContent: 'center',
                      ...raised,
                    }}
                  >
                    <MessageIcon size={24} strokeWidth={1.75} color={colors.textOnAction} />
                  </Pressable>
                )}
              </YStack>
              {/* snapPoints -- verified live against production: without
                  it, the sheet's own default left a large empty area
                  below the composer's short content instead of the
                  "full-content" fit its own doc comment describes. 55%
                  of screen height fits the TextArea, the photo picker
                  row, and the Reply button with room for the
                  error-message state (was 40% before the picker). */}
              <Sheet open={composerOpen} onOpenChange={setComposerOpen} snapPoints={[55]}>
                <ReplyComposer
                  replyBody={replyBody}
                  onChangeReplyBody={setReplyBody}
                  canReply={canReply}
                  reply={reply}
                  attachments={attachments}
                />
              </Sheet>
            </>
          ) : null}
        </YStack>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}

// The page's lead element -- larger, heavier type than anything below
// it (the post cards, the composer), per this project's "one thing
// leads" composition rule. Matches the profile screen's page-heading
// treatment for consistency.
function ThreadHeader({
  thread,
  viewerCoords,
  currentUserId,
  replyCount,
  voteProps,
  router,
}: {
  thread: ThreadListItem;
  viewerCoords: Coordinates | undefined;
  currentUserId: string | undefined;
  replyCount: number | undefined;
  voteProps: ComponentProps<typeof VoteControl>;
  router: ReturnType<typeof useRouter>;
}) {
  // undefined until the viewer's own profile coordinates have loaded
  // -- the timestamp line below renders on its own in that case,
  // matching ThreadCard's identical "omit rather than block" treatment
  // (screens/threads/index.tsx).
  const distanceLabel = viewerCoords
    ? formatDistance(distanceMeters(viewerCoords, { lat: thread.lat, lng: thread.lng }))
    : undefined;

  return (
    <YStack pt="$2" pb="$4" gap="$2">
      {/* Issue #291: was author -> vote -> title/body -- the page's
          one engagement affordance sat ABOVE the content it asks you
          to react to, a real hierarchy inversion (you were asked to
          vote before you'd read the post). Author leads now, vote
          moves down to its own row alongside the reply count, after
          the content. The creator gets a two-line identity block
          (handle + a "started this thread" label) rather than
          PostCard's plain single-line row below -- the distinct
          treatment the thread's own lead element needs so a reply
          author is never mistaken for the person who started it. The
          overflow menu (issue #291: Block/Report used to be two
          equal-weight plain-text links under the meta, a token-by-
          intent violation and ambient threat framing on a neutral
          read) rides the SAME row as the identity, not a separate
          block below the content. */}
      <XStack alignItems="center" justifyContent="space-between">
        <Pressable
          accessibilityRole="button"
          {...hit({ vertical: slopFor(36) })}
          onPress={() => router.push({ pathname: '/users/[userId]', params: { userId: thread.creatorId } })}
          style={{ flexShrink: 1 }}
        >
          <XStack alignItems="center" gap="$2">
            <PseudonymousAvatar handle={thread.creatorHandle} id={thread.creatorId} size={36} />
            {/* flexShrink on both the wrapping YStack and the handle Text
                -- handle has no length limit anywhere in the stack, and an
                XStack row doesn't shrink its children by default, so an
                unusually long handle would push this block wider than the
                screen (final review, PR #52). */}
            <YStack flexShrink={1}>
              <Text
                color="$color"
                fontSize="$meta"
                lineHeight="$meta"
                fontWeight="$semibold"
                flexShrink={1}
                numberOfLines={1}
              >
                {displayHandle(thread.creatorHandle)}
              </Text>
              {/* Issue #148 AC4: was the shared Eyebrow component
                  (uppercase, mono, letter-spaced) -- corrected away from
                  that treatment: this is attribution META text under a
                  name, not a categorical tag/label the way Wordmark's
                  brand mark or the landing page's "01/02/03" step numbers
                  are, and uppercase+letter-spacing overstated its role.
                  13px (issue #148 AC3's own "meta at 13px" tier), body
                  font, sentence case. */}
              <Text
                color="$colorTertiary"
                fontSize="$caption"
                lineHeight="$caption"
                fontWeight="$medium"
                maxFontSizeMultiplier={1.5}
              >
                Started this thread
              </Text>
            </YStack>
          </XStack>
        </Pressable>
        {currentUserId !== undefined && currentUserId !== thread.creatorId && (
          <OverflowMenu
            actions={[
              {
                label: 'Report',
                onPress: () =>
                  router.push({
                    pathname: '/report/[targetType]/[targetId]',
                    params: { targetType: 'thread', targetId: thread.id },
                  }),
              },
              {
                label: 'Block',
                onPress: () => router.push({ pathname: '/block/[userId]', params: { userId: thread.creatorId } }),
                destructive: true,
              },
            ]}
          />
        )}
      </XStack>
      {/* Issue #212: title/body previously sat directly against each
          other with minimal gap -- with short body content this read
          as a duplicated heading rather than a clear title-then-body
          hierarchy. pt/gap added for real visual separation. */}
      <YStack gap="$2" pt="$1">
        <Heading>{thread.title}</Heading>
        <Paragraph color="$colorSecondary">
          <Linkified>{thread.body}</Linkified>
        </Paragraph>
        <AttachmentList attachments={thread.attachments ?? []} width="fill" />
      </YStack>
      {/* One meta line, not two -- distance joins the existing
          timestamp rather than adding a third piece of metadata below
          the identity block, matching ThreadCard's identical treatment
          (screens/threads/index.tsx). Omitted (not a placeholder) when
          viewerCoords hasn't loaded. */}
      <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
        {relativeTime(thread.createdAt)}
        {distanceLabel ? ` · ${distanceLabel}` : ''}
      </Text>
      {/* Issue #291's own reorder target: vote + reply count as one
          row, after every bit of the thread's own content -- votinga
          is now something you do AFTER reading, not before. replyCount
          is the already-fetched posts list's own length (this screen
          fetches the complete reply list for the FlatList below it
          regardless, see fetchAllPosts -- no extra request), undefined
          only for the instant before that query resolves. */}
      <XStack alignItems="center" gap="$3">
        <VoteControl {...voteProps} />
        {replyCount !== undefined && (
          <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
            {replyCount === 1 ? '1 reply' : `${replyCount} replies`}
          </Text>
        )}
      </XStack>
      <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta" fontWeight="$semibold" pt="$2">
        Replies
      </Text>
    </YStack>
  );
}

function PostCard({
  post,
  currentUserId,
  router,
  voteProps,
}: {
  post: PostItem;
  currentUserId: string | undefined;
  router: ReturnType<typeof useRouter>;
  voteProps: ComponentProps<typeof VoteControl>;
}) {
  return (
    // Issue #296, global density pass -- same p="$3" tightening as
    // ThreadCard (threads/index.tsx), coordinated together per that
    // issue's own scope note. See that file's own doc comment for the
    // full reasoning (founder's real-device feedback, WCAG 2.5.8 floor
    // left untouched).
    <YStack {...cardStyle} p="$3" gap="$1">
      {/* First line, above the reply body -- matches ThreadCard's
          identity row (screens/threads/index.tsx) so a post reads as
          authored by someone, the same way it already reads as the
          same brand material via cardStyle. The identity row itself is
          now tappable (navigates to the author's profile) even though
          the card body isn't -- same "tap the identity, not the whole
          card" pattern as ThreadHeader above. */}
      <XStack alignItems="center" justifyContent="space-between" pb="$2">
        <Pressable
          accessibilityRole="button"
          {...hit({ vertical: slopFor(28) })}
          onPress={() => router.push({ pathname: '/users/[userId]', params: { userId: post.authorId } })}
          style={{ flexShrink: 1 }}
        >
          <XStack alignItems="center" gap="$2">
            <PseudonymousAvatar handle={post.authorHandle} id={post.authorId} size={28} />
            <Text
              color="$colorSecondary"
              fontSize="$meta"
              lineHeight="$meta"
              fontWeight="$semibold"
              textAlign="left"
              flexShrink={1}
              numberOfLines={1}
            >
              {displayHandle(post.authorHandle)}
            </Text>
            {/* Same "You" tell as ThreadCard (threads/index.tsx) -- see
                that file's own comment for why a Badge, not a colored
                border. */}
            {currentUserId !== undefined && currentUserId === post.authorId && (
              <Badge variant="primary" paddingHorizontal="$2" paddingVertical="$0_5">
                You
              </Badge>
            )}
          </XStack>
        </Pressable>
        {/* Issue #206/#291: Block/Report used to render as two plain-
            text links directly under the identity row (then moved below
            the body, #206) -- either way, still competing for attention
            as bare text. Same overflow-menu treatment as ThreadHeader's
            own author row now: present, reachable, not ambient. */}
        {currentUserId !== undefined && currentUserId !== post.authorId && (
          <OverflowMenu
            actions={[
              {
                label: 'Report',
                onPress: () =>
                  router.push({
                    pathname: '/report/[targetType]/[targetId]',
                    params: { targetType: 'post', targetId: post.id },
                  }),
              },
              {
                label: 'Block',
                onPress: () => router.push({ pathname: '/block/[userId]', params: { userId: post.authorId } }),
                destructive: true,
              },
            ]}
          />
        )}
      </XStack>
      <Text color="$color" textAlign="left">
        <Linkified>{post.body}</Linkified>
      </Text>
      <AttachmentList attachments={post.attachments ?? []} width="fill" />
      <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta" textAlign="left">
        {relativeTime(post.createdAt)}
      </Text>
      <XStack pt="$1">
        <VoteControl {...voteProps} />
      </XStack>
    </YStack>
  );
}

// Issue #147 AC3: the whole-thread first-load state used a bare,
// centered ActivityIndicator (this screen's evidence line in that
// issue's own audit inventory). One larger skeleton for the thread's
// own title/body, plus two reply-shaped ones below, so the placeholder
// roughly previews the real layout that's about to load in.
function ThreadDetailSkeleton() {
  return (
    <YStack gap="$3" pt="$4" flex={1}>
      <Skeleton height={140} />
      <Skeleton height={56} />
      <Skeleton height={56} />
    </YStack>
  );
}

// Covers the posts query's own pending/error/empty states -- kept
// independent of the thread query above it (see ThreadDetail: the
// thread's title/body render as soon as it loads, regardless of
// whether its replies are still loading or failed to).
function PostsListStatus({ posts }: { posts: ReturnType<typeof useQuery<PostItem[]>> }) {
  const accentColor = useGlassAccentColor();

  if (posts.isPending) {
    // Issue #147 AC3: replies are shorter, denser rows than a thread
    // card -- 56px vs. threads/index.tsx's 92px skeleton -- so the
    // placeholder's own proportions still read as "reply," not just a
    // reused generic block.
    return (
      <YStack py="$4" gap="$2">
        <Skeleton height={56} />
        <Skeleton height={56} />
      </YStack>
    );
  }

  if (posts.isError) {
    return (
      <EmptyState
        flex={0}
        justifyContent="flex-start"
        py="$6"
        gap="$3"
        icon={<AlertIcon size={20} strokeWidth={1.75} color={accentColor} />}
        iconSize={48}
        title="Couldn't load replies"
        description="Check your connection and try again."
        action={{ label: 'Try again', size: 'sm', onPress: () => posts.refetch() }}
      />
    );
  }

  return (
    <EmptyState
      flex={0}
      justifyContent="flex-start"
      py="$6"
      gap="$1"
      icon={<ThreadIcon size={20} strokeWidth={1.75} color={accentColor} />}
      iconSize={48}
      title="No replies yet."
      description="Be the first — say hello."
    />
  );
}

function ReplyComposer({
  replyBody,
  onChangeReplyBody,
  canReply,
  reply,
  attachments,
}: {
  replyBody: string;
  onChangeReplyBody: (value: string) => void;
  canReply: boolean;
  reply: ReturnType<typeof useMutation<PostItem, Error, void>>;
  attachments: ReturnType<typeof useAttachmentPicker>;
}) {
  // Issue #291: was its own bordered/background box pinned to the
  // bottom of the screen, its own color and corner-radius treatment
  // reading as jarring against the rest of the UI (founder, real
  // iPhone screenshot, both light and dark mode) -- now opens as
  // content INSIDE the FAB's Sheet, which already supplies a real
  // $card surface, rounded top corners, and a border (sheet.tsx's own
  // Frame). This component is just the form now, no second surface
  // layered on top of the sheet's own. Also drops the standalone
  // "Reply" Label that used to sit directly above the "Reply" button
  // -- the sheet only ever opens FOR replying (triggered by a reply-
  // labeled FAB), so restating the word a second time right above a
  // button that already says it was pure duplication (#291's own
  // explicit ask).
  return (
    <YStack gap="$2">
      {reply.isError && (
        <Paragraph color="$destructive" textAlign="center" fontSize="$meta" lineHeight="$meta">
          {reply.error.message || "Couldn't post your reply. Check your connection and try again."}
        </Paragraph>
      )}
      <TextArea
        id="reply-body"
        accessibilityLabel="Reply"
        value={replyBody}
        onChangeText={onChangeReplyBody}
        placeholder="Share something with this thread..."
        minHeight={80}
        maxLength={MAX_POST_BODY_LEN}
        autoFocus
      />
      <AttachmentPicker picker={attachments} locked={reply.isPending} />
      <PrimaryButton
        size="sm"
        alignSelf="flex-end"
        disabled={!canReply}
        loading={reply.isPending}
        onPress={() => {
          if (!reply.isPending && canReply) reply.mutate();
        }}
      >
        {reply.isPending ? <ActivityIndicator color="white" /> : 'Reply'}
      </PrimaryButton>
    </YStack>
  );
}
